'use client';
import type { StateCreator } from 'zustand';
import type { ChangePreviewViewDataType, OrderInfo, PaymentInfoType, PayStatustype, ProductType } from '@/app/types';

// 支付状态接口
interface PaymentState {
  payStatus: PayStatustype; // 订单状态（包含loading状态）
  orderInfo?: OrderInfo; // 订单信息
  payProduct?: ProductType; // 当前购买产品
  payQuantity?: number; // 当前购买数量（仅 claw_pass_bundle 等支持数量的场景使用）
  /**
   * 当前购买时用户选择的优惠券 id：传 string 指定该券，传 null/undefined 表示不使用优惠券。
   * 用于变更流程跨组件传递（StripeConfirmDialog 选券 → fetchChangePreview → ChangeContent → changeSubscription）
   */
  payCouponId?: string | null;
  confirmedChangeData?: ChangePreviewViewDataType; // 确认变更信息
  paymentInfo?: PaymentInfoType; // 支付详情
  paymentPopupState: boolean; // 是否打开支付弹窗
  refreshGetProducts: boolean; // 是否刷新获取产品列表
  couponSummary: { unusedCount: number; nearestUnusedExpireAt: string | null } | null; // 优惠券概览（banner 与 dialog 共享）
  maxProductDiscount: number; // 所有套餐中最大折扣百分比（来自 getPriceDiscount）
}

export interface PayStore extends PaymentState {
  // 关闭支付弹窗
  closePayment: (callback?: (status: PayStatustype) => void) => void;
  // 二维码超时
  handleQrcodeTimeout: () => void;
  // 状态更新方法
  updatePaymentState: (updates: Partial<PaymentState>) => void;
  // 重置支付状态
  resetPaymentState: () => void;
  // 更新获取产品列表
  updateRefreshGetProducts: (value: boolean) => void;
  updateCouponSummary: (value: PaymentState['couponSummary']) => void;
  updateMaxProductDiscount: (value: number) => void;
}

const initialState: PaymentState = {
  payStatus: 'unspecified',
  paymentPopupState: false,
  refreshGetProducts: false,
  couponSummary: null,
  maxProductDiscount: 0,
};

export const createPaySlice: StateCreator<PayStore, [], [], PayStore> = (set, get) => ({
  ...initialState,

  // 更新支付状态
  updatePaymentState: (updates) => {
    set((state) => ({ ...state, ...updates }));
  },

  // 关闭支付弹窗
  closePayment: (callback) => {
    const state = get();
    set({
      payStatus: 'unspecified',
      paymentPopupState: false,
      payQuantity: undefined,
      payCouponId: undefined,
    });
    // 执行回调
    callback ? callback(state.payStatus) : null;
  },

  // 二维码超时
  handleQrcodeTimeout: () => {
    set({
      payStatus: 'closed',
    });
  },

  // 重置支付状态
  resetPaymentState: () => {
    set({ ...initialState, paymentPopupState: false });
  },

  // 获取产品列表
  updateRefreshGetProducts: (value: boolean) => {
    set({ refreshGetProducts: value });
  },

  updateCouponSummary: (value) => {
    set({ couponSummary: value });
  },

  updateMaxProductDiscount: (value: number) => {
    set({ maxProductDiscount: value });
  },
});
